/**
 * Отдельный vuex модуль для взаимодействия по analytics/transport_detection-API
 */

import {ANALYTICS, AnalyticSettings} from "@/store/analytics/helpers.js";
import makeMessage from "@/store/analytics/analyticMessage.js";

// actions для transport_detection. Вызов действия начинать с "analytics/transportDetections/"
export const ACTION_LOAD_TRANSPORT_DETECTIONS_SETTINGS = "LOAD_TRANSPORT_DETECTIONS_SETTINGS";
export const ACTION_LOAD_TRANSPORT_DETECTION_SETTINGS = "LOAD_TRANSPORT_DETECTION_SETTINGS";
export const ACTION_LOAD_TRANSPORT_DETECTION_EVENTS = "LOAD_TRANSPORT_DETECTION_EVENTS";
export const ACTION_LOAD_TRANSPORT_DETECTION_EVENT = "LOAD_TRANSPORT_DETECTION_EVENT";
export const ACTION_LOAD_TRANSPORT_DETECTION_REPORT = "LOAD_TRANSPORT_DETECTION_REPORT";

/**
 * Класс для представления настроек аналитики доступа к мусоркам.
 */
export class TransportDetectionSettings extends AnalyticSettings {
  get analyticType() {
    return ANALYTICS.transport_detection;
  }
}

export default {
  namespaced: true,
  state: {},
  mutations: {},
  actions: {
    /**
     * Загрузка списка настроек аналитик доступа к мусоркам.
     *
     * @param {Object} context
     * @param {Array.<String>} camerasNumbers
     * @return {Promise.<Array.<TransportDetectionSettings>>}
     */
    async [ACTION_LOAD_TRANSPORT_DETECTIONS_SETTINGS](context, {camerasNumbers}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/transport_detection/settings/", {camera_numbers: camerasNumbers});
        return response.data.results.map(rawTransportDetectionSettings => new TransportDetectionSettings(rawTransportDetectionSettings));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка настроек аналитики доступа к мусоркам с конкретной камеры.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @return {Promise.<TransportDetectionSettings>}
     */
    async [ACTION_LOAD_TRANSPORT_DETECTION_SETTINGS]({dispatch}, {cameraNumber}) {
      const transportDetectionsSettings = await dispatch(ACTION_LOAD_TRANSPORT_DETECTIONS_SETTINGS, {camerasNumbers: [cameraNumber]});
      if (_.isEmpty(transportDetectionsSettings)) {
        return null;
      }
      return transportDetectionsSettings[0];
    },
    /**
     * Загрузка перечня событий по аналитике доступа к мусоркам с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Array.<Number>} eventIds
     * @return {Promise.<Array.<TransportDetectionMessage>>}
     */
    async [ACTION_LOAD_TRANSPORT_DETECTION_EVENTS](context, {cameraNumber, eventIds}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/transport_detection/events/", {
          camera_number: cameraNumber,
          event_ids: eventIds,
        });
        return response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.transport_detection));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка перечня событий по аналитике доступа к мусоркам с камеры для формирования отчета.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @param {Number} eventId
     * @return {Promise.<TransportDetectionMessage>}
     */
    async [ACTION_LOAD_TRANSPORT_DETECTION_EVENT]({dispatch}, {cameraNumber, eventId}) {
      const transportDetectionsSettings = await dispatch(ACTION_LOAD_TRANSPORT_DETECTION_EVENTS, {cameraNumber, eventIds: [eventId]});
      if (_.isEmpty(transportDetectionsSettings)) {
        return null;
      }
      return transportDetectionsSettings[0];
    },
    /**
     * Загрузка перечня событий по определению доступа к мусоркам с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @param {String} query
     * @return {Promise.<Array.<TransportDetectionMessage>>}
     */
    async [ACTION_LOAD_TRANSPORT_DETECTION_REPORT](context, {cameraNumber, archiveFrom, archiveTo, query = null, page, page_size, cancelToken}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/transport_detection/report/", {
          camera_number: cameraNumber,
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          query: query,
          page: page,
          page_size: page_size
        }, {cancelToken});
        const messages = response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.transport_detection));

        return {
          messages,
          response: response.data
        };
      } catch {
        return {
          messages: [],
          response: null
        };
      }
    },
  },
};
